import {useState, useRef, useCallback, useEffect, useLayoutEffect} from 'react';
import {useChekinSDK} from 'context/ChekinSDK';
import type {
  KioskoWebSocketResponse,
  KioskoConnectionState,
  KioskoEventHandlers,
} from '../types';
import {getFeatureFlags} from 'utils/features';

const MAX_RECONNECT_ATTEMPTS = 2;
const RECONNECT_DELAY = 2000;

const SuccessPrintedCardActions = [
  'fake_print_card_success',
  'print_card_success',
  'move_to_gate',
];
const PrintedCardActionsWorkFlow = [
  'move_to_stacker',
  'move_to_rf',
  'salto_command',
  'move_to_gate',
];
const SuccessScannerActions = ['fake_scanner_success', 'scanner_success'];
const ErrorPrintedCardActions = ['fake_print_card_error', 'print_card_error'];

interface UseKioskoConnectionProps {
  autoConnect?: boolean;
  apiKey?: string;
  onConnectionStateChange?: (state: Partial<KioskoConnectionState>) => void;
}

export const useKioskoConnection = ({
  autoConnect = true,
  apiKey = 'kiosk-demo-key-2025',
  onConnectionStateChange,
}: UseKioskoConnectionProps = {}) => {
  const {connectKioskoWebSocket} = useChekinSDK();

  const [connectionState, setConnectionState] = useState<KioskoConnectionState>({
    isConnected: false,
    isConnecting: false,
    error: null,
    reconnectAttempts: 0,
  });

  const updateConnectionState = useCallback(
    (state: Partial<KioskoConnectionState>) => {
      setConnectionState(prev => {
        const newState = {...prev, ...state};
        return newState;
      });

      if (onConnectionStateChange) {
        onConnectionStateChange(state);
      }
    },
    [onConnectionStateChange],
  );

  const socketRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const eventHandlersRef = useRef<Set<KioskoEventHandlers>>(new Set());

  const notifyEventHandlers = useCallback((response: KioskoWebSocketResponse) => {
    eventHandlersRef.current.forEach(handlers => {
      if (response.action === 'scanner_initialize' && handlers.onScannerInitialized) {
        handlers.onScannerInitialized();
      }
      if (response.action === 'scan_document' && handlers.onDocumentScanned) {
        handlers.onDocumentScanned(response);
      }

      if (SuccessScannerActions.includes(response.action) && handlers.onDocumentScanned) {
        handlers.onDocumentScanned(response);
      }

      if (
        SuccessPrintedCardActions.includes(response.action) &&
        handlers.onPrintedCard &&
        response.success
      ) {
        handlers.onPrintedCard();
      }
      if (
        [...ErrorPrintedCardActions, ...PrintedCardActionsWorkFlow].includes(
          response.action,
        ) &&
        handlers.onPrintedCardError &&
        !response.success
      ) {
        handlers.onPrintedCardError();
      }

      if (handlers.onMessage) {
        handlers.onMessage(response);
      }
    });
  }, []);

  const connect = useCallback(() => {
    const isFeatureEnabled = getFeatureFlags('IT_4334_KIOSKO_MVP');
    if (!isFeatureEnabled) {
      return;
    }

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (socketRef.current?.readyState === WebSocket.OPEN) {
      return;
    }

    if (socketRef.current) {
      socketRef.current.close();
    }

    try {
      const socket = connectKioskoWebSocket();
      socketRef.current = socket;

      socket.onopen = () => {
        console.log('✅ Kiosko WebSocket connected');
        reconnectAttemptsRef.current = 0;
        updateConnectionState({
          isConnected: true,
          isConnecting: false,
          reconnectAttempts: 0,
        });
      };

      socket.onclose = event => {
        console.log('🔌 Kiosko WebSocket disconnected', event.code);
        updateConnectionState({
          isConnected: false,
          isConnecting: false,
        });

        if (
          autoConnect &&
          event.code !== 1000 &&
          reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS
        ) {
          reconnectAttemptsRef.current++;
          console.log(
            `🔄 Reconnecting ${reconnectAttemptsRef.current}/${MAX_RECONNECT_ATTEMPTS}`,
          );

          updateConnectionState({
            reconnectAttempts: reconnectAttemptsRef.current,
          });

          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, RECONNECT_DELAY);
        }
      };

      socket.onerror = event => {
        console.error('❌ Kiosko WebSocket error:', event);
        // const error = new Error('WebSocket connection failed');
        // updateConnectionState({
        //   isConnected: false,
        //   isConnecting: false,
        //   error,
        // });
      };

      socket.onmessage = event => {
        try {
          const response = JSON.parse(event.data) as KioskoWebSocketResponse;
          notifyEventHandlers(response);
        } catch (err) {
          console.error('Error processing Kiosko message:', err);
        }
      };
    } catch (err) {
      console.error('Failed to create Kiosko WebSocket:', err);
      const error = err instanceof Error ? err : new Error('Connection failed');
      updateConnectionState({
        isConnecting: false,
        error,
      });
    }
  }, [connectKioskoWebSocket, autoConnect, notifyEventHandlers, updateConnectionState]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    reconnectAttemptsRef.current = MAX_RECONNECT_ATTEMPTS;

    if (socketRef.current) {
      socketRef.current.close(1000, 'Manual disconnect');
      socketRef.current = null;
      updateConnectionState({
        isConnected: false,
        reconnectAttempts: MAX_RECONNECT_ATTEMPTS,
      });
    }
  }, [updateConnectionState]);

  const sendMessage = useCallback(
    (action: string, params: Record<string, unknown> = {}, messageApiKey?: string) => {
      if (!socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) {
        console.warn('⚠️ Kiosko WebSocket not connected');
        return false;
      }

      try {
        const message = {
          action,
          params,
          api_key: messageApiKey || apiKey,
          timestamp: new Date().toISOString(),
        };

        socketRef.current.send(JSON.stringify(message));
        return true;
      } catch (err) {
        console.error('Error sending Kiosko message:', err);
        return false;
      }
    },
    [apiKey],
  );

  const subscribeToEvents = useCallback((handlers: KioskoEventHandlers) => {
    eventHandlersRef.current.add(handlers);

    return () => {
      eventHandlersRef.current.delete(handlers);
    };
  }, []);

  useLayoutEffect(() => {
    if (autoConnect && !connectionState.isConnected && !connectionState.isConnecting) {
      connect();
    }
  }, [autoConnect, connectionState.isConnected, connectionState.isConnecting, connect]);

  useEffect(() => {
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (socketRef.current) {
        socketRef.current.close();
      }
    };
  }, []);

  return {
    ...connectionState,
    disconnect,
    sendMessage,
    subscribeToEvents,
  };
};
